import { type KeyboardEventHandler } from "react";
import { Check, Hotel, Search, X } from "lucide-react";
import { COLORS } from "@/design-system/tokens";
import Checkbox from "@/components/ui/checkbox";
import { Property } from "./FetchProperties";

export const EmptySearch = ({ isMobile }: { isMobile: boolean }) => (
  <div
    className={`flex flex-col items-center gap-2 text-center ${
      isMobile ? "py-4" : "py-2"
    }`}
  >
    <Search size={isMobile ? 20 : 18} className="text-slate-300" />
    <span
      className={`${
        isMobile ? "text-[0.9375rem]" : "text-[0.8125rem]"
      } text-slate-400`}
    >
      No properties found
    </span>
  </div>
);

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  isMobile: boolean;
  autoFocus?: boolean;
  // Single-select drives its listbox highlight from here, since the field keeps
  // focus while the rows below are navigated.
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  ariaProps?: Record<string, string | boolean | undefined>;
}

export const SearchField = ({
  value,
  onChange,
  isMobile,
  autoFocus = false,
  onKeyDown,
  ariaProps,
}: SearchFieldProps) => (
  <div
    className={`flex items-center gap-1 rounded-[10px] border border-slate-200 bg-slate-50 ${
      isMobile ? "px-[6px] py-[3.5px]" : "mx-2 mt-2 mb-1 px-[6px] py-1"
    }`}
  >
    <Search
      size={isMobile ? 15 : 14}
      color="#94A3B8"
      style={{ flexShrink: 0 }}
    />
    <input
      autoFocus={autoFocus}
      placeholder="Search properties..."
      aria-label="Search properties"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      {...ariaProps}
      className={`min-w-0 flex-1 bg-transparent text-slate-800 outline-none placeholder:text-slate-400 ${
        isMobile ? "text-[0.9375rem]" : "text-[0.8125rem]"
      }`}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        className="flex shrink-0 items-center justify-center border-0 bg-transparent p-[1px] text-slate-400 hover:text-slate-600"
      >
        <X size={isMobile ? 14 : 13} />
      </button>
    )}
  </div>
);

export const SectionHeader = ({
  label,
  count,
  isMobile,
  onSelectAll,
}: {
  label: string;
  count: number;
  isMobile: boolean;
  onSelectAll?: () => void;
}) => (
  <div
    className={`flex items-center ${
      isMobile ? "gap-1.5 px-2 pt-1 pb-0.5" : "gap-1 px-[6px] pt-[3px] pb-[1px]"
    }`}
  >
    <span
      className={`shrink-0 font-bold uppercase tracking-[0.08em] text-slate-400 ${
        isMobile ? "text-[0.6875rem]" : "text-[0.625rem]"
      }`}
    >
      {label}
    </span>
    <div className="h-px flex-1 bg-slate-100" />
    <span
      className={`shrink-0 rounded-full bg-slate-100 font-semibold text-slate-600 ${
        isMobile
          ? "px-2 py-px text-[0.6875rem]"
          : "px-1.5 py-px text-[0.625rem]"
      }`}
    >
      {count}
    </span>
    {onSelectAll && (
      <span
        onClick={onSelectAll}
        className={`shrink-0 cursor-pointer select-none font-semibold text-primary hover:text-primary-hover ${
          isMobile ? "text-[0.6875rem]" : "text-[0.625rem]"
        }`}
      >
        Select all
      </span>
    )}
  </div>
);

export const PropertyRow = ({
  property,
  isMobile,
  checked,
  onClick,
  // Single-select reuses this row without the checkbox column, and drives a
  // keyboard highlight that is distinct from `checked`.
  showCheckbox = true,
  optionId,
  highlighted = false,
  onMouseEnter,
}: {
  property: Property;
  isMobile: boolean;
  checked: boolean;
  onClick: () => void;
  showCheckbox?: boolean;
  optionId?: string;
  highlighted?: boolean;
  onMouseEnter?: () => void;
}) => {
  const iconSize = isMobile ? 34 : 28;
  const rowStateClass = checked
    ? isMobile
      ? "bg-primary-medium active:bg-[#B3C9E3]"
      : "bg-primary-medium hover:bg-[#B3C9E3]"
    : isMobile
    ? "bg-transparent active:bg-slate-100"
    : "bg-transparent hover:bg-slate-50";
  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      id={optionId}
      role={optionId ? "option" : undefined}
      aria-selected={optionId ? checked : undefined}
      className={`relative flex cursor-pointer items-center ${
        highlighted ? "ring-2 ring-inset ring-primary/35 " : ""
      }${
        isMobile
          ? "mx-1 my-0.5 gap-1.5 rounded-[10px] px-2 py-[5px] transition-colors duration-100"
          : "mx-0.5 my-[2px] gap-1 rounded-[9px] px-[6px] py-[3px] transition-all duration-150 hover:z-10 hover:scale-[1.035] hover:shadow-cardHover"
      } ${rowStateClass}`}
    >
      {showCheckbox && (
        <Checkbox
          checked={checked}
          aria-label={`${property.propertyName} (${property.propertyId})`}
        />
      )}
      <div
        className="flex shrink-0 items-center justify-center transition-colors duration-150"
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: isMobile ? "8px" : "6px",
          backgroundColor: checked ? "#B3C9E3" : COLORS.background.muted,
        }}
      >
        <Hotel
          size={isMobile ? 16 : 14}
          color={checked ? COLORS.primary : "#94A3B8"}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={`m-0 overflow-hidden text-ellipsis whitespace-nowrap leading-[1.3] ${
            isMobile ? "text-[0.9375rem]" : "text-[0.8125rem]"
          } ${
            checked
              ? "font-semibold text-slate-800"
              : "font-medium text-slate-700"
          }`}
        >
          {property.propertyName}
        </p>
        <p
          className={`m-0 font-medium tracking-[0.02em] ${
            checked ? "text-slate-600" : "text-slate-400"
          } ${isMobile ? "text-[0.75rem]" : "text-[0.6875rem]"}`}
          style={{ fontFamily: "'Roboto Mono', monospace" }}
        >
          {property.propertyId}
        </p>
      </div>
      {checked && (
        <Check
          size={isMobile ? 15 : 14}
          color={COLORS.primary}
          style={{ flexShrink: 0 }}
        />
      )}
    </div>
  );
};

// Shared list body for both the desktop dropdown and mobile sheet — same structure, isMobile-driven spacing only.
interface PropertyListContentProps {
  isMobile: boolean;
  filteredPortfolio: Property[];
  filteredNonPortfolio: Property[];
  showSelectAllPortfolio: boolean;
  isChecked: (property: Property) => boolean;
  onToggleProperty: (property: Property) => void;
  onSelectAllPortfolio: () => void;
}

export const PropertyListContent = ({
  isMobile,
  filteredPortfolio,
  filteredNonPortfolio,
  showSelectAllPortfolio,
  isChecked,
  onToggleProperty,
  onSelectAllPortfolio,
}: PropertyListContentProps) => {
  if (filteredPortfolio.length === 0 && filteredNonPortfolio.length === 0) {
    return <EmptySearch isMobile={isMobile} />;
  }

  return (
    <>
      {filteredPortfolio.length > 0 && (
        <div
          className={`mb-1 rounded-[10px] ${isMobile ? "mx-2" : "mx-0.5"}`}
          style={{ backgroundColor: COLORS.primaryLight }}
        >
          <SectionHeader
            label="My Portfolio"
            count={filteredPortfolio.length}
            isMobile={isMobile}
            onSelectAll={
              showSelectAllPortfolio ? onSelectAllPortfolio : undefined
            }
          />
          {filteredPortfolio.map((p) => (
            <PropertyRow
              key={p.propertyId}
              property={p}
              isMobile={isMobile}
              checked={isChecked(p)}
              onClick={() => onToggleProperty(p)}
            />
          ))}
        </div>
      )}
      {filteredNonPortfolio.length > 0 && (
        <>
          {filteredPortfolio.length > 0 && (
            <div
              className={`my-0.5 border-t border-slate-100 ${
                isMobile ? "mx-2" : "mx-[6px]"
              }`}
            />
          )}
          {filteredPortfolio.length > 0 && (
            <SectionHeader
              label="Other Properties"
              count={filteredNonPortfolio.length}
              isMobile={isMobile}
            />
          )}
          {filteredNonPortfolio.map((p) => (
            <PropertyRow
              key={p.propertyId}
              property={p}
              isMobile={isMobile}
              checked={isChecked(p)}
              onClick={() => onToggleProperty(p)}
            />
          ))}
        </>
      )}
    </>
  );
};

export const SelectionLimitWarning = ({ max }: { max: number }) => (
  <div className="shrink-0 border-t border-orange-200 bg-orange-50 px-[6px] py-[3px]">
    <p className="m-0 text-[0.8125rem] leading-[1.4] text-amber-800">
      Only {max} properties can be selected at a time. Deselect one to add
      another.
    </p>
  </div>
);

interface DropdownFooterProps {
  pendingCount: number;
  onClear: () => void;
  onApply: () => void;
  isMobile: boolean;
}

export const DropdownFooter = ({
  pendingCount,
  onClear,
  onApply,
  isMobile,
}: DropdownFooterProps) => (
  <div
    className={`flex shrink-0 items-center justify-between shadow-stickyBar ${
      isMobile
        ? "px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+16px)]"
        : "px-3 py-2.5"
    }`}
  >
    <span
      className={`text-slate-400 ${
        isMobile ? "text-[0.875rem]" : "text-[0.75rem]"
      }`}
    >
      {pendingCount === 0 ? "None selected" : `${pendingCount} selected`}
    </span>
    <div className="flex items-center gap-2">
      {pendingCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          className={`rounded-md border border-slate-300 bg-white font-semibold text-slate-600 transition-colors hover:border-slate-800 hover:bg-slate-800 hover:text-white ${
            isMobile
              ? "px-4 py-2.5 text-[0.9375rem]"
              : "px-3 py-1.5 text-[0.8125rem]"
          }`}
        >
          {isMobile ? "Clear" : "Deselect all"}
        </button>
      )}
      <button
        type="button"
        onClick={onApply}
        className={`rounded-md bg-primary font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.08),0_6px_14px_-6px_rgba(0,74,152,0.16)] transition-all hover:-translate-y-px hover:bg-primary-hover hover:shadow-[0_2px_4px_rgba(0,0,0,0.1),0_10px_20px_-6px_rgba(0,74,152,0.16)] ${
          isMobile
            ? "px-5 py-2.5 text-[0.9375rem]"
            : "px-4 py-1.5 text-[0.8125rem]"
        }`}
      >
        Apply
      </button>
    </div>
  </div>
);
